import { useEffect, useMemo, useState } from 'react';
import api from '../api/axios';
import { MATERIAL_TABS } from '../pages/user/materialFieldConfig';
import { VENDOR_TABS } from '../pages/user/vendorFieldConfig';
import { toast } from 'react-toastify';
import { checkGstConsistency, gstinPan, isValidGstin, stateNameFor, gstinStateCode } from '../lib/gst';
import useVendorOptions from '../hooks/useVendorOptions';
import useMaterialOptions from '../hooks/useMaterialOptions';
import VendorFieldInput, { resolveFieldOptions } from './VendorFieldInput';

const TABS_BY_SOURCE = {
  material_requests: MATERIAL_TABS,
  vendor_requests: VENDOR_TABS,
};

const CODE_COLUMNS = {
  material_requests: 'material_number',
  vendor_requests: 'business_partner_number',
};

const fmt = v => {
  if (v === null || v === undefined || v === '') return null;
  if (v instanceof Date) return v.toISOString().slice(0, 10);
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
};

const diffOf = (draft, request, keys) =>
  keys.reduce((acc, k) => {
    const from = request[k] === null || request[k] === undefined ? '' : String(request[k]);
    const to = draft[k] === null || draft[k] === undefined ? '' : String(draft[k]);
    if (from !== to) acc[k] = draft[k] === '' ? null : draft[k];
    return acc;
  }, {});

const RequestDetail = ({ request, editable = false, onSaved }) => {
  const tabs = TABS_BY_SOURCE[request?.source];
  const [active, setActive] = useState(0);
  const [draft, setDraft] = useState({});
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [ifscStatus, setIfscStatus] = useState(null);

  const isVendor   = request?.source === 'vendor_requests';
  const isMaterial = request?.source === 'material_requests';

  const vendorOptions   = useVendorOptions(isVendor);
  const materialOptions = useMaterialOptions(isMaterial);

  const requestKey = request ? `${request.source}-${request.id}` : null;

  useEffect(() => {
    setDraft({});
    setNote('');
    setActive(0);
    setIfscStatus(null);
  }, [requestKey]);

  // Re-fetch plant-dependent options when plant changes
  const plantVal = isMaterial ? String((draft.plant ?? request?.plant) || '') : '';
  useEffect(() => {
    if (!isMaterial) return;
    materialOptions.refetchPlantDependents(plantVal);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plantVal, isMaterial]);

  // Re-fetch sales-area-dependent options when sales org changes
  const salesOrgVal = isMaterial ? String((draft.sales_organization ?? request?.sales_organization) || '') : '';
  useEffect(() => {
    if (!isMaterial) return;
    materialOptions.refetchSalesAreaDependents(salesOrgVal);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [salesOrgVal, isMaterial]);

  // IFSC auto-fill (vendor)
  const ifscValue = isVendor ? String((draft.bank_key_ifsc ?? request?.bank_key_ifsc) || '') : '';
  useEffect(() => {
    if (!isVendor || !ifscValue) { setIfscStatus(null); return; }
    const found = vendorOptions.bankKeys.find(b => b.code === ifscValue);
    if (found) {
      setDraft(prev => ({ ...prev, bank_id: found.description, bank_country_region_key: found.country || 'IN' }));
      setIfscStatus('ok');
    } else { setIfscStatus(null); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ifscValue, vendorOptions.bankKeys, isVendor]);

  // Re-fetch planning groups when company_code changes (vendor)
  const companyCodeVal = isVendor ? String((draft.company_code ?? request?.company_code) || '') : '';
  useEffect(() => {
    if (!isVendor) return;
    vendorOptions.refetchPlanningGroups(companyCodeVal);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyCodeVal, isVendor]);

  const allKeys = useMemo(
    () => (tabs ? tabs.flatMap(t => t.fields.map(f => f.key)) : []),
    [tabs]
  );

  const valueOf = key => (key in draft ? draft[key] : request?.[key]) ?? '';

  const gstProblems = useMemo(
    () =>
      isVendor
        ? checkGstConsistency({
            region: valueOf('region'),
            pan: valueOf('pan_number'),
            gstin: valueOf('gstn_number'),
          })
        : [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isVendor, draft.region, draft.pan_number, draft.gstn_number, request?.region, request?.pan_number, request?.gstn_number]
  );

  const gstin = valueOf('gstn_number');

  if (!tabs) {
    if (!request?.data_payload) return null;
    return <pre style={S.pre}>{JSON.stringify(request.data_payload, null, 2)}</pre>;
  }

  const codeColumn = CODE_COLUMNS[request.source];

  const activeTab = tabs[active] || tabs[0];
  const rows = activeTab.fields
    .map(f => ({ label: f.label, value: fmt(request[f.key]) }))
    .filter(r => r.value !== null);

  const changes = diffOf(draft, request, allKeys);
  const changeCount = Object.keys(changes).length;

  const setField = (key, value) => setDraft(prev => ({ ...prev, [key]: value }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/requests/${request.id}/corrections`, {
        source: request.source,
        changes,
        comment: note.trim() || undefined,
      });
      toast.success(`Saved ${changeCount} correction${changeCount === 1 ? '' : 's'}`);
      setDraft({});
      setNote('');
      onSaved?.(data);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not save corrections');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={S.wrap}>
      <div style={S.tabBar}>
        {tabs.map((t, i) => {
          const filled = t.fields.filter(f => fmt(request[f.key]) !== null).length;
          const touched = t.fields.filter(f => f.key in draft).length;
          const isActive = i === active;
          return (
            <button key={t.id} type="button" onClick={() => setActive(i)}
              style={{ ...S.tab, ...(isActive ? { ...S.tabActive, boxShadow: 'inset 0 -3px 0 #4f6ef7' } : null) }}>
              {t.label}
              {filled > 0 && <span style={S.tabCount}>{filled}</span>}
              {touched > 0 && <span style={{ ...S.tabCount, background: '#fef3c7', color: '#b45309' }}>{touched}*</span>}
            </button>
          );
        })}
      </div>

      {editable ? (
        <div style={S.editWrap}>
          <div style={S.editHint}>
            ✎ Edit any field below and save. The system-generated {codeColumn.replace(/_/g, ' ')} is IT-only.
          </div>
          <div style={S.grid}>
            {activeTab.fields.map(f => {
              const locked = f.key === codeColumn;
              const changed = f.key in changes;
              const fieldOptions = resolveFieldOptions(f.key, request.source, vendorOptions, materialOptions);
              return (
                <div key={f.key} style={S.editField}>
                  <label style={S.editLabel}>
                    {f.label}
                    {locked && <span style={S.lockTag}>IT only</span>}
                    {changed && <span style={S.changedTag}>changed</span>}
                  </label>
                  {locked ? (
                    <input
                      style={{ ...S.editInput, ...S.editInputLocked }}
                      disabled
                      value={request[f.key] ?? ''}
                      placeholder="Filled in by IT at completion"
                    />
                  ) : (
                    <div style={changed ? { outline: '2px solid #f59e0b', borderRadius: 8 } : {}}>
                      <VendorFieldInput
                        field={f}
                        value={valueOf(f.key)}
                        onChange={v => setField(f.key, v)}
                        options={fieldOptions}
                        ifscStatus={ifscStatus}
                        readOnly={false}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {isVendor && isValidGstin(gstin) && (
            <div style={S.gstBox}>
              <div style={S.gstHead}>
                GSTIN reads as {stateNameFor(gstinStateCode(gstin))} · PAN {gstinPan(gstin)}
              </div>
              {gstProblems.length === 0 ? (
                <div style={S.gstOk}>Region and PAN agree with the GSTIN.</div>
              ) : (
                gstProblems.map(p => <div key={p} style={S.gstProblem}>⚠ {p}</div>)
              )}
            </div>
          )}

          {changeCount > 0 && (
            <div style={S.saveBox}>
              <div style={S.saveCount}>{changeCount} unsaved correction{changeCount === 1 ? '' : 's'}</div>
              <input
                style={S.noteInput}
                value={note}
                onChange={e => setNote(e.target.value)}
                placeholder="Optional note — posted to the thread so the requester sees why"
              />
              <div style={S.saveRow}>
                <button type="button" onClick={save} disabled={saving} style={{ ...S.btnPrimary, opacity: saving ? 0.6 : 1 }}>
                  {saving ? 'Saving…' : 'Save corrections'}
                </button>
                <button type="button" onClick={() => { setDraft({}); setNote(''); }} style={S.btnGhost}>Discard</button>
              </div>
            </div>
          )}
        </div>
      ) : rows.length === 0 ? (
        <div style={S.empty}>No {activeTab.label} data provided</div>
      ) : (
        <div style={S.rows}>
          {rows.map(r => (
            <div key={r.label} style={S.row}>
              <span style={S.rowKey}>{r.label}</span>
              <span style={S.rowVal}>{r.value}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const S = {
  wrap: { marginBottom: 14 },
  tabBar: { display: 'flex', flexWrap: 'wrap', gap: 2, marginBottom: 10, borderBottom: '1px solid #eef1f7' },
  tab: { background: 'none', border: 'none', cursor: 'pointer', padding: '6px 9px', fontSize: 11, fontWeight: 700, color: '#8892b0', whiteSpace: 'nowrap' },
  tabActive: { color: '#0B1554', background: '#f8faff' },
  tabCount: { display: 'inline-block', minWidth: 14, padding: '0 4px', marginLeft: 4, borderRadius: 7, background: '#eef1f7', color: '#8892b0', fontSize: 9.5, fontWeight: 700, textAlign: 'center' },
  rows: { display: 'grid', gridTemplateColumns: '1fr', gap: 2 },
  row: { display: 'flex', justifyContent: 'space-between', gap: 10, padding: '4px 0', borderBottom: '1px solid #f6f8fc' },
  rowKey: { fontSize: 11.5, color: '#64748b', flexShrink: 0, maxWidth: '55%' },
  rowVal: { fontSize: 12.5, color: '#0B1554', fontWeight: 600, textAlign: 'right', wordBreak: 'break-word' },
  empty: { fontSize: 12.5, color: '#94a3b8', padding: '10px 0' },
  pre: { background: '#f8faff', border: '1px solid #e5e7eb', borderRadius: 8, padding: 10, fontSize: 11, overflow: 'auto', maxHeight: 200 },
  editWrap: { marginTop: 4 },
  gstBox: { marginTop: 4, padding: '10px 12px', borderRadius: 10, background: '#f8faff', border: '1px solid #dde4fb' },
  gstHead: { fontSize: 11.5, fontWeight: 800, color: '#0B1554', marginBottom: 6 },
  gstOk: { fontSize: 11.5, color: '#166534', lineHeight: 1.5 },
  gstProblem: { fontSize: 11.5, color: '#9a3412', lineHeight: 1.5, marginTop: 3 },
  editHint: { fontSize: 11.5, color: '#92400e', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '8px 10px', marginBottom: 12 },
  grid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' },
  editField: { marginBottom: 10 },
  editLabel: { display: 'block', fontSize: 11.5, color: '#475569', marginBottom: 4, fontWeight: 600 },
  lockTag: { marginLeft: 6, fontSize: 9, fontWeight: 800, letterSpacing: 0.5, color: '#64748b', background: '#eef1f7', padding: '1px 5px', borderRadius: 4 },
  changedTag: { marginLeft: 6, fontSize: 9, fontWeight: 800, letterSpacing: 0.5, color: '#b45309', background: '#fef3c7', padding: '1px 5px', borderRadius: 4 },
  editInput: { width: '100%', padding: '8px 10px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 13, color: '#0B1554', background: '#f8faff', boxSizing: 'border-box' },
  editInputLocked: { background: '#eef1f7', color: '#94a3b8', cursor: 'not-allowed' },
  saveBox: { marginTop: 12, padding: '12px 14px', borderRadius: 10, background: '#f8faff', border: '1px solid #dde4fb' },
  saveCount: { fontSize: 11.5, fontWeight: 800, color: '#0B1554', marginBottom: 8 },
  noteInput: { width: '100%', padding: '9px 11px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 13, color: '#0B1554', background: '#fff', boxSizing: 'border-box', marginBottom: 10 },
  saveRow: { display: 'flex', gap: 8 },
  btnPrimary: { flex: 1, padding: '10px', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer', background: 'linear-gradient(135deg,#0d1a6e,#4f6ef7)' },
  btnGhost: { padding: '10px 18px', color: '#64748b', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', background: '#fff' },
};

export default RequestDetail;
